<template>
  <div
    :class="[
      'question-content',
      compact ? 'question-content--compact' : 'p-20 pb-30'
    ]"
  >
    <!-- 题目标题 -->
    <div :class="compact ? 'mb-12' : 'mb-20'">
      <div
        :class="
          compact
            ? 'text-14 font-bold line-height-18 mb-4'
            : 'text-16 font-bold line-height-22 mb-8'
        "
      >
        {{ question.title }}
      </div>
      <div :class="compact ? 'text-11 text-gray' : 'text-12 text-gray'">
        {{ questionType === 'single' ? '(单选题)' : '(多选题)' }}
      </div>
    </div>

    <!-- 选项列表 - 单选 -->
    <van-radio-group v-if="questionType === 'single'" v-model="userAnswer">
      <div
        :class="[
          'question-options',
          useFullscreenMode ? 'question-options--grid' : ''
        ]"
      >
        <div
          v-for="(option, index) in question.options"
          :key="option.id"
          :class="[
            compact ? 'mb-4' : 'mb-12',
            useFullscreenMode ? 'question-options__item' : ''
          ]"
        >
          <div
            :class="[
              'relative rounded-10 transition-all',
              compact ? 'p-8' : 'p-15',
              compact
                ? userAnswer === option.id
                  ? 'bg-orange-50'
                  : 'bg-gray-F1F4F8'
                : userAnswer === option.id
                ? 'bg-white border-2 border-orange'
                : 'bg-gray-F1F4F8 border-2 border-transparent',
              !compact && isAnswered && getOptionClass(option.id)
            ]"
            @click="selectOption(option.id)"
          >
            <tk-flex :gutter="12" class="items-start">
              <van-radio
                :name="option.id"
                :disabled="isAnswered"
                checked-color="#FC6554"
              />
              <div
                :class="
                  compact
                    ? 'flex-1 text-13 line-height-18'
                    : 'flex-1 text-14 line-height-22'
                "
              >
                <span class="font-bold mr-4">{{ getOptionPrefix(index) }}.</span
                >{{ option.label }}
              </div>
              <!-- 答题反馈图标 -->
              <div
                v-if="isAnswered && showFeedbackIcon(option.id)"
                class="ml-8"
              >
                <van-icon
                  v-if="isCorrectOption(option.id)"
                  name="success"
                  color="#07C160"
                  size="20"
                />
                <van-icon
                  v-else-if="userAnswer === option.id"
                  name="close"
                  color="#F8604E"
                  size="20"
                />
              </div>
            </tk-flex>
          </div>
        </div>
      </div>
    </van-radio-group>

    <!-- 选项列表 - 多选 -->
    <van-checkbox-group v-else v-model="userAnswers" :disabled="isAnswered">
      <div
        :class="[
          'question-options',
          useFullscreenMode ? 'question-options--grid' : ''
        ]"
      >
        <div
          v-for="(option, index) in question.options"
          :key="option.id"
          :class="[
            compact ? 'mb-4' : 'mb-12',
            useFullscreenMode ? 'question-options__item' : ''
          ]"
        >
          <div
            :class="[
              'relative rounded-10 transition-all',
              compact ? 'p-8' : 'p-15',
              compact
                ? userAnswers.includes(option.id)
                  ? 'bg-orange-50'
                  : 'bg-gray-F1F4F8'
                : userAnswers.includes(option.id)
                ? 'bg-white border-2 border-orange'
                : 'bg-gray-F1F4F8 border-2 border-transparent',
              !compact && isAnswered && getOptionClass(option.id)
            ]"
            @click="toggleCheckbox(option.id)"
          >
            <tk-flex :gutter="12" class="items-start">
              <van-checkbox :name="option.id" checked-color="#FC6554" />
              <div
                :class="
                  compact
                    ? 'flex-1 text-13 line-height-18'
                    : 'flex-1 text-14 line-height-22'
                "
              >
                <span class="font-bold mr-4">{{ getOptionPrefix(index) }}.</span
                >{{ option.label }}
              </div>
              <!-- 答题反馈图标 -->
              <div
                v-if="isAnswered && showFeedbackIcon(option.id)"
                class="ml-8"
              >
                <van-icon
                  v-if="isCorrectOption(option.id)"
                  name="success"
                  color="#07C160"
                  size="20"
                />
                <van-icon
                  v-else-if="userAnswers.includes(option.id)"
                  name="close"
                  color="#F8604E"
                  size="20"
                />
              </div>
            </tk-flex>
          </div>
        </div>
      </div>
    </van-checkbox-group>

    <!-- 答题反馈 -->
    <div v-if="isAnswered" :class="compact ? 'mt-10 mb-10' : 'mt-20 mb-20'">
      <tk-flex middle :gutter="8" :class="compact ? 'mb-6' : 'mb-8'">
        <van-icon
          :name="isCorrect ? 'success' : 'info'"
          :color="isCorrect ? '#07C160' : '#F8604E'"
          :size="compact ? '16' : '18'"
        />
        <div
          :class="[
            'font-bold',
            compact ? 'text-13' : 'text-14',
            isCorrect ? 'text-green-600' : 'text-red'
          ]"
        >
          {{ isCorrect ? '回答正确' : '回答错误' }}
        </div>
      </tk-flex>
      <div
        v-if="!isCorrect"
        :class="compact ? 'text-12 text-gray-666' : 'text-13 text-gray-666'"
      >
        正确答案：{{ correctAnswerText }}
      </div>
    </div>

    <!-- 提交/完成按钮 -->
    <div :class="compact ? 'mt-10' : 'mt-20'">
      <div
        v-if="!isAnswered"
        :class="[
          'w-full rounded-10 flex items-center justify-center font-bold cursor-pointer transition-all',
          compact ? 'h-36 text-14' : 'h-44 text-16',
          canSubmit
            ? 'bg-orange text-white'
            : 'bg-gray-F1F4F8 text-gray cursor-not-allowed'
        ]"
        @click="submitAnswer"
      >
        提交答案
      </div>
      <div
        v-else
        :class="[
          'w-full rounded-10 flex items-center justify-center bg-orange text-white font-bold cursor-pointer',
          compact ? 'h-36 text-14' : 'h-44 text-16'
        ]"
        @click="handleComplete"
      >
        继续播放
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionContent',
  props: {
    question: {
      type: Object,
      default: () => ({
        id: '',
        time: 0,
        title: '',
        options: [],
        answers: []
      })
    },
    isFullscreen: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      userAnswer: '', // 单选答案
      userAnswers: [], // 多选答案
      isAnswered: false, // 是否已答题
      isCorrect: false // 答案是否正确
    }
  },
  computed: {
    useFullscreenMode() {
      return this.isFullscreen
    },
    // 根据答案数量动态判断题目类型：一个答案视为单选，多于一个视为多选
    questionType() {
      if (!this.question || !Array.isArray(this.question.answers)) {
        return 'single'
      }
      return this.question.answers.length > 1 ? 'multiple' : 'single'
    },
    canSubmit() {
      if (this.questionType === 'single') {
        return this.userAnswer !== ''
      } else {
        return this.userAnswers.length > 0
      }
    },
    correctAnswerText() {
      if (!this.question || !Array.isArray(this.question.answers)) {
        return ''
      }
      if (!Array.isArray(this.question.options)) {
        return this.question.answers.join('、')
      }
      const labels = this.question.answers.map((id) => {
        const option = this.question.options.find((item) => item.id === id)
        return option ? option.label : id
      })
      return labels.join('、')
    }
  },
  watch: {
    // 当题目变化时重置状态
    question: {
      handler() {
        this.resetAnswers()
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    selectOption(optionId) {
      if (!this.isAnswered && this.questionType === 'single') {
        this.userAnswer = optionId
      }
    },
    toggleCheckbox(optionId) {
      if (!this.isAnswered && this.questionType === 'multiple') {
        const index = this.userAnswers.indexOf(optionId)
        if (index > -1) {
          this.userAnswers.splice(index, 1)
        } else {
          this.userAnswers.push(optionId)
        }
      }
    },
    submitAnswer() {
      if (!this.canSubmit) return

      this.isAnswered = true

      // 判断答案是否正确
      if (this.questionType === 'single') {
        this.isCorrect = Array.isArray(this.question.answers)
          ? this.question.answers.includes(this.userAnswer)
          : false
      } else {
        // 多选题：选项数量相同且都包含
        const sortedUserAnswers = [...this.userAnswers].sort()
        const sortedCorrectAnswers = Array.isArray(this.question.answers)
          ? [...this.question.answers].sort()
          : []
        this.isCorrect =
          sortedUserAnswers.length === sortedCorrectAnswers.length &&
          sortedUserAnswers.every(
            (ans, idx) => ans === sortedCorrectAnswers[idx]
          )
      }
    },
    handleComplete() {
      this.$emit('complete', {
        isCorrect: this.isCorrect,
        userAnswer:
          this.questionType === 'single' ? this.userAnswer : this.userAnswers
      })
    },
    resetAnswers() {
      this.userAnswer = ''
      this.userAnswers = []
      this.isAnswered = false
      this.isCorrect = false
    },
    isCorrectOption(optionId) {
      return Array.isArray(this.question.answers)
        ? this.question.answers.includes(optionId)
        : false
    },
    showFeedbackIcon(optionId) {
      // 显示反馈图标的条件：
      // 1. 用户选择了这个选项
      // 2. 或者这是正确答案但用户没选
      const userSelected =
        this.questionType === 'single'
          ? this.userAnswer === optionId
          : this.userAnswers.includes(optionId)
      return userSelected || this.isCorrectOption(optionId)
    },
    getOptionClass(optionId) {
      if (!this.isAnswered) return ''

      const isCorrect = this.isCorrectOption(optionId)
      const userSelected =
        this.questionType === 'single'
          ? this.userAnswer === optionId
          : this.userAnswers.includes(optionId)

      if (isCorrect && userSelected) {
        return 'border-green-500'
      } else if (!isCorrect && userSelected) {
        return 'border-red'
      } else if (isCorrect) {
        return 'border-green-500'
      }
      return ''
    },
    getOptionPrefix(index) {
      // 将索引转换为字母前缀 A、B、C、D...
      return String.fromCharCode(65 + index)
    }
  }
}
</script>

<style scoped>
.question-content {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  position: relative;
  box-sizing: border-box;
}

.question-content--compact {
  padding: 14px 16px 20px;
}

.bg-orange-50 {
  background-color: #fff5f3;
}

.question-options {
  /* 非全屏默认一列，由模板中的 margin 控制间距 */
}

.question-options--grid {
  display: flex;
  flex-wrap: wrap;
  margin-right: -8px;
}

.question-options__item {
  width: 50%;
  box-sizing: border-box;
  padding-right: 8px;
}

/* 修复 Vant 勾选框高度限制 */
.question-content >>> .van-radio__icon,
.question-content >>> .van-checkbox__icon {
  height: auto !important;
}

.text-gray-666 {
  color: #666666;
}

.text-green-600 {
  color: #07c160;
}

.transition-all {
  transition: all 0.3s ease;
}
</style>
